import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Nav } from "@/components/Shell";
import { Orb } from "@/components/Orb";
import { Composer } from "@/components/Composer";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const BG = "https://images.pexels.com/photos/10313412/pexels-photo-10313412.jpeg?auto=compress&cs=tinysrgb&w=1920";
const ease = [0.22, 1, 0.36, 1];

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const started = useRef(false);

  const begin = async (text, voice = false) => {
    if (!user) {
      if (text) sessionStorage.setItem("feerd_pending", text);
      if (voice) sessionStorage.setItem("feerd_pending_voice", "1");
      navigate("/auth");
      return;
    }
    try {
      const { data } = await api.post("/conversations", {});
      navigate(`/c/${data.id}`, { state: { initial: text, voice } });
    } catch (e) { toast.error(errMsg(e)); }
  };

  useEffect(() => {
    if (!user || started.current) return;
    const pending = sessionStorage.getItem("feerd_pending");
    const pv = sessionStorage.getItem("feerd_pending_voice");
    if (pending || pv) {
      started.current = true;
      sessionStorage.removeItem("feerd_pending");
      sessionStorage.removeItem("feerd_pending_voice");
      begin(pending, !!pv);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0A0A0A]" data-testid="home-page">
      <div className="absolute inset-0 bg-cover bg-center opacity-[0.22] scale-105" style={{ backgroundImage: `url(${BG})` }} aria-hidden="true" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_38%,transparent_0%,rgba(10,10,10,0.7)_45%,#0A0A0A_80%)]" aria-hidden="true" />
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#0A0A0A] to-transparent" aria-hidden="true" />
      <div className="absolute left-[8%] top-[22%] h-24 w-24 rounded-full orb-core opacity-30 blur-[1px] hidden md:block" aria-hidden="true" />
      <div className="absolute right-[12%] top-[58%] h-10 w-10 rounded-full orb-core opacity-40 hidden md:block" aria-hidden="true" />

      <div className="relative z-10 flex flex-col min-h-screen">
        <Nav transparent />
        <main className="flex-1 grid md:grid-cols-12 gap-8 px-6 md:px-12 pb-10 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease, delay: 0.2 }} className="md:col-span-5 order-2 md:order-1">
            <p className="eyebrow mb-6">A personal intelligence · v0.1</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-ivory" data-testid="home-headline">
              Speak, and the<br /><em className="text-[#D8BFD8] font-normal">room</em> listens.
            </h1>
            <p className="mt-8 text-sm md:text-base text-[#8A8A8A] leading-relaxed max-w-md">
              Feerd converses, remembers only what you allow, and carries research into the background while you are elsewhere. It never claims to have done what it hasn't.
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.6, ease }} className="md:col-span-7 order-1 md:order-2 flex justify-center md:justify-end md:pr-[6%] pt-6 md:pt-0">
            <div className="hidden md:block"><Orb state="idle" size={340} /></div>
            <div className="md:hidden"><Orb state="idle" size={190} /></div>
          </motion.div>
        </main>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease, delay: 0.6 }} className="px-6 md:px-12 pb-10 md:pb-14">
          <div className="max-w-3xl">
            <Composer onSend={(t) => begin(t)} onMic={() => begin(null, true)} autoFocus placeholder="Ask, or say “research the best monsoon treks near Pune”…" />
            <p className="mt-4 text-[0.68rem] tracking-[0.18em] uppercase text-[#7a7a7a]">Enter to send · Mic for voice · Research runs as a background task</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
